import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useAtom } from 'jotai'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useGetInvitation, useSignUpInvite } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput'
import Spinner from '@/components/spinner'
import { useToast } from '@/components/toast/useToast'

import { authTokenAtom } from '@/state/authToken'

import { mapProblemDetailErrors, passwordSchema } from '@/utils/validation.ts'

const acceptInviteFormSchema = z.intersection(
    passwordSchema,
    z.object({
        name: z.string(),
        email: z.string().email(),
    }),
)

type AcceptInviteFormSchema = z.infer<typeof acceptInviteFormSchema>

interface Props {
    inviteToken: string
}

export default function AcceptInviteForm({ inviteToken }: Props) {
    const [, setAuthToken] = useAtom(authTokenAtom)
    const { toast } = useToast()
    const navigate = useNavigate()
    const invite = useGetInvitation(inviteToken)
    const signUpInviteMutator = useSignUpInvite(inviteToken)

    const form = useForm<AcceptInviteFormSchema>({
        resolver: zodResolver(acceptInviteFormSchema),
        mode: 'onBlur',
    })
    if (invite.isPending) {
        return <Spinner size="sm" navVisible={false} className="text-center" />
    }

    if (!invite.data) {
        return <p>No invite found</p>
    }

    const inviteData = invite.data
    const onSubmit = (values: AcceptInviteFormSchema) => {
        signUpInviteMutator.mutate(
            {
                ...values,
                inviteToken,
            },
            {
                onSuccess: data => {
                    setAuthToken(data.token)
                    navigate({
                        to: '/',
                        search: {
                            welcome: true,
                        },
                    })
                },
                // @ts-expect-error - Kubb generated code is not typed correctly
                onError: (error: AxiosError) => {
                    mapProblemDetailErrors(error, form.formState.errors)
                    toast({
                        variant: 'destructive',
                        title: 'Invite acceptance failed!',
                        description: `Failed to accept invite, please try again`,
                    })
                },
            },
        )
    }

    return (
        <>
            <p className="block mb-2 text-center">
                🎉 You've been invited to join {inviteData.clientName} by {inviteData.inviterName}, how exciting! Let's
                get you signed up.
            </p>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="pt-4 space-y-4">
                    <FormInput
                        control={form.control}
                        name="name"
                        label="Name"
                        placeholder="John Doe"
                        autoComplete="name"
                    />
                    <FormInput control={form.control} name="email" label="Email" placeholder="you@example.com" />
                    <FormInput control={form.control} name="password" type="password" label="Password" />
                    <FormInput control={form.control} name="passwordConfirm" type="password" label="Password" />

                    <div className="flex items-center justify-between">
                        <Button type="submit" disabled={!form.formState.isValid}>
                            {signUpInviteMutator.isPending ? 'Signing up' : 'Sign up'}
                        </Button>
                        <Link to={`/login`} search={{ redirect: `/invite/${inviteToken}` }} className="text-sm">
                            Or login to an existing account
                        </Link>
                    </div>
                </form>
            </Form>
        </>
    )
}
